import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebase/config';
import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';

// Importaciones del Contexto y Utilidades Centralizadas (Pilares del Canvas)
import { useSegurosData } from '../../context/SegurosDataContext';

// Importamos la lista de estados maestros de renovación y motivos de no renovación
import { estadoGestionRenovacion as listaEstados, motivoNoRenueva as listaMotivosNoRenueva } from '../../data/listasDesplegables';

/**
 * EditarRenovacion - Gestión del estado intermedio de renovaciones de pólizas patrimoniales.
 * Refactorizado bajo arquitectura limpia con consumo de caché global y estilos utilitarios Tailwind.
 */
const EditarRenovacion = ({ show, onClose, poliza, freshClientData, onSuccess }) => {
    const { dbMasters } = useSegurosData();

    const [estadoGestion, setEstadoGestion] = useState('');
    const [gestionadoPor, setGestionadoPor] = useState('');
    const [motivoNoRenovacion, setMotivoNoRenovacion] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [clientData, setClientData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingClient, setIsLoadingClient] = useState(false);

    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])]
            .map(c => c.nombre)
            .sort((a, b) => a.localeCompare(b));
    }, [dbMasters?.colaboradores]);

    const estadosFiltrados = useMemo(() => {
        return (listaEstados || []).filter(estado => estado !== 'RENOVADA');
    }, []);

    useEffect(() => {
        if (show && poliza) {
            setEstadoGestion(poliza.estadoGestionRenovacion || '');
            setGestionadoPor(poliza.gestionadoPor || '');
            setMotivoNoRenovacion(poliza.motivoNoRenueva || '');
            setObservaciones(poliza.observacionesRenovacion || '');
            setClientData(freshClientData || null);

            // Cargar datos extendidos del cliente si no vinieron por props
            if (!freshClientData && poliza.clienteId) {
                const fetchClientData = async () => {
                    setIsLoadingClient(true);
                    try {
                        const snap = await getDoc(doc(db, "public/data/clientes", poliza.clienteId));
                        if (snap.exists()) {
                            setClientData(snap.data());
                        }
                    } catch (e) {
                        console.error("Error al cargar datos extendidos de cliente en renovación:", e);
                    } finally {
                        setIsLoadingClient(false);
                    }
                };
                fetchClientData();
            }
        }
    }, [show, poliza, freshClientData]);

    // Limpieza de estados al cerrar para evitar fugas visuales de información
    useEffect(() => {
        if (!show) {
            setClientData(null);
            setEstadoGestion('');
            setGestionadoPor('');
            setMotivoNoRenovacion('');
            setObservaciones('');
        }
    }, [show]);

    // Handler reactivo del estado de gestión para limpiar el motivo si no es NO RENUEVA
    const handleEstadoGestionChange = (nuevoEstado) => {
        setEstadoGestion(nuevoEstado);
        if (nuevoEstado !== 'NO RENUEVA') {
            setMotivoNoRenovacion('');
        }
    };

    const isDataMissing = !poliza || (!freshClientData && !clientData && isLoadingClient);
    const esNoRenueva = estadoGestion === 'NO RENUEVA';

    const handleSave = async (e) => {
        e.preventDefault();
        if (isSaving || !poliza?.path) return;
        
        setIsSaving(true);
        try {
            const polizaRef = doc(db, poliza.path);

            const updateData = {
                estadoGestionRenovacion: estadoGestion || null,
                gestionadoPor: gestionadoPor || null,
                motivoNoRenueva: esNoRenueva ? (motivoNoRenovacion || null) : null,
                observacionesRenovacion: observaciones || null,
                fechaUltimaGestion: serverTimestamp(),
                bloqueo: null // Quitamos el bloqueo temporal si estaba activo
            };

            await updateDoc(polizaRef, updateData);

            if (onSuccess) {
                onSuccess('Gestión actualizada correctamente', { ...poliza, ...updateData });
            }
            onClose();
        } catch (error) {
            console.error("Error crítico al actualizar la gestión de renovación:", error);
        } finally {
            setIsSaving(false);
        }
    };

    if (!show || !poliza) return null;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-sans">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden border border-slate-100 max-h-[90vh] animate-fade-in transition-all duration-300">
                
                {/* CABECERA DEL MODAL */}
                <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100 bg-blue-50/50 shrink-0 select-none">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3 font-['Inter'] leading-none">
                        <span className="bg-blue-100 text-blue-600 p-2.5 rounded-xl">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </span>
                        Editar gestión de renovación
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-white shadow-sm">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>

                {/* CUERPO DEL FORMULARIO */}
                <div className="overflow-y-auto p-8 bg-white relative scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent flex-grow">
                    {isDataMissing && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 z-50 backdrop-blur-[1px]">
                            <div className="w-12 h-12 border-4 border-slate-100 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                            <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest text-center italic">Sincronizando información...</p>
                        </div>
                    )}

                    <form id="editarRenovacionForm" onSubmit={handleSave} className="space-y-6 text-left">
                        {/* SECCIÓN I: GESTIÓN INTERNA */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                            <legend className="px-3 text-base font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight select-none">Gestión interna</legend>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-3 text-left">
                                {/* Estado de gestión */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-2 tracking-tight uppercase pl-1 select-none">Estado de gestión</label>
                                    <select 
                                        value={estadoGestion} 
                                        onChange={(e) => handleEstadoGestionChange(e.target.value)} 
                                        className="w-full h-12 px-4 border border-slate-200 rounded-xl outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 bg-white font-medium text-slate-700 transition-all cursor-pointer"
                                    >
                                        <option value="">Sin seleccionar...</option>
                                        {estadosFiltrados.map((est, i) => (
                                            <option key={i} value={est}>{est}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Gestionado por */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-2 tracking-tight uppercase pl-1 select-none">Gestionado por</label>
                                    <select 
                                        value={gestionadoPor} 
                                        onChange={(e) => setGestionadoPor(e.target.value)} 
                                        className="w-full h-12 px-4 border border-slate-200 rounded-xl outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 bg-white font-medium text-slate-700 transition-all cursor-pointer"
                                    >
                                        <option value="">Seleccione colaborador...</option>
                                        {sortedColaboradores.map((nombre, i) => (
                                            <option key={i} value={nombre}>{nombre}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Motivo de no renovación (Condicionado a NO RENUEVA) */}
                                {esNoRenueva && (
                                    <div className="md:col-span-2 text-left animate-fade-in">
                                        <label className="block text-xs font-bold text-rose-600 mb-2 tracking-tight uppercase pl-1 select-none flex items-center gap-1.5">
                                            <span>Motivo de no renovación</span>
                                            <span className="text-[10px] text-rose-400 font-normal normal-case">(Requerido para estadísticas)</span>
                                        </label>
                                        <select 
                                            value={motivoNoRenovacion} 
                                            onChange={(e) => setMotivoNoRenovacion(e.target.value)} 
                                            required={esNoRenueva}
                                            className="w-full h-12 px-4 border border-rose-200 bg-rose-50/20 rounded-xl outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-200 font-medium text-slate-700 transition-all cursor-pointer"
                                        >
                                            <option value="">Seleccione motivo de baja...</option>
                                            {(listaMotivosNoRenueva || []).map((motivo, i) => (
                                                <option key={i} value={motivo}>{motivo}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Observaciones de gestión */}
                                <div className="md:col-span-2 text-left">
                                    <label className="block text-xs font-bold text-slate-500 mb-2 tracking-tight uppercase pl-1 select-none">Observaciones de gestión</label>
                                    <textarea 
                                        value={observaciones} 
                                        onChange={(e) => setObservaciones(e.target.value)} 
                                        className="w-full p-4 border border-slate-200 rounded-xl outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 bg-white resize-none font-medium text-slate-700 min-h-[140px] transition-all placeholder:text-slate-300" 
                                        placeholder="Escriba aquí los avances de la renovación de la póliza..." 
                                    />
                                </div>
                            </div>
                        </fieldset>
                    </form>
                </div>

                {/* BOTONERA DE ACCIONES INFERIOR */}
                <div className="px-8 py-5 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 shrink-0 select-none">
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold hover:bg-slate-100 shadow-sm text-xs uppercase tracking-wider transition-all transform active:scale-95"
                    >
                        Cancelar
                    </button>
                    <button 
                        type="submit" 
                        form="editarRenovacionForm"
                        disabled={isSaving || isDataMissing} 
                        className="px-10 py-2.5 rounded-full bg-blue-600 text-white font-bold hover:bg-blue-700 shadow-lg shadow-blue-100 disabled:opacity-50 text-xs uppercase tracking-wider transform active:scale-95 min-w-[180px] flex justify-center items-center"
                    >
                        {isSaving ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default EditarRenovacion;